/**
 * PATH: packages/ui/src/kanban/api-fetch/progressive-snapshot.ts
 * FILE_ID: UI-KANBAN-FETCH-PRIORITY-SNAPSHOT-001
 * PURPOSE: Optional transport for the additive priority snapshot endpoint.
 * WHY: Keep the board API factory below the 500-line boundary.
 * FLOW: validated options -> repeated lane_id query -> shared abortable request.
 */
import type {
  GetProgressiveBoardSnapshotOptions,
  KanbanApi,
  ProgressiveBoardSnapshot,
} from '../api'
import type { ApiFetchContext } from './context'

function progressiveSnapshotQuery(opts?: GetProgressiveBoardSnapshotOptions) {
  const laneIds = opts?.laneIds
  if (laneIds) {
    if (
      laneIds.length > 3 ||
      laneIds.some((laneId) => typeof laneId !== 'string' || !laneId.trim()) ||
      new Set(laneIds).size !== laneIds.length
    ) {
      throw new RangeError(
        'Priority snapshot laneIds must be up to three unique, non-blank ids',
      )
    }
  }
  if (
    opts?.perLaneLimit !== undefined &&
    (!Number.isInteger(opts.perLaneLimit) ||
      opts.perLaneLimit < 1 ||
      opts.perLaneLimit > 32)
  ) {
    throw new RangeError('Priority snapshot perLaneLimit must be an integer from 1 to 32')
  }
  return {
    lane_id: laneIds,
    per_lane_limit:
      opts?.perLaneLimit === undefined ? undefined : String(opts.perLaneLimit),
  }
}

export function createProgressiveSnapshotApi(ctx: ApiFetchContext) {
  const { _request } = ctx
  return {
    getProgressiveBoardSnapshot: async (
      boardId: string,
      opts?: GetProgressiveBoardSnapshotOptions,
    ) => {
      const query = progressiveSnapshotQuery(opts)
      return _request<ProgressiveBoardSnapshot>(
        'GET',
        `/boards/${boardId}/progressive-snapshot/`,
        undefined,
        query,
        undefined,
        {
          signal: opts?.signal,
          // Additive endpoint: FE may ship before BE. A 404 means "not
          // deployed yet" — fall back to legacy board load without toast spam.
          quietStatuses: [404],
        },
      )
    },
  } satisfies Partial<KanbanApi>
}
